import React, { useState, useCallback } from 'react';
import { View, Text, Pressable, Alert } from 'react-native';
import { YStack, XStack, Paragraph, Button } from 'tamagui';
import { Feather, MaterialIcons } from '@expo/vector-icons';
import { FlatList } from "react-native";

import apis from '@api/api';
import DeleteConfirmModal from '@components/modals/DeleteConfirmModal/DeleteConfirmModal';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';

/**
 * TaskGroup component - renders a list of tasks for a work package
 */
const TaskGroup = ({
  item,
  projectId,
  workPackageId,
  minify = false,
  modifyCallback,
  refresh,
  taskRef
}) => {
  const theme = themeSelector();
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);

  const handleDeleteTask = useCallback(async () => {
    if (!taskToDelete) return;

    try {
      await apis.deleteTask({
        taskId: taskToDelete._id,
        workPackageId,
        projectId
      });

      // Update the task list by removing the deleted task
      const updatedTaskList = (item.task_list || []).filter(
        task => task._id !== taskToDelete._id
      );

      if (modifyCallback) {
        modifyCallback(updatedTaskList, workPackageId);
      }

      if (refresh) {
        refresh();
      }

      setDeleteModalVisible(false);
      setTaskToDelete(null);
    } catch (error) {
      console.error('Failed to delete task:', error);
      Alert.alert('Error', 'Failed to delete task. Please try again.');
    }
  }, [taskToDelete, workPackageId, projectId, item.task_list, modifyCallback, refresh]);

  const handleAddTask = useCallback(() => {
    if (taskRef?.current?.openModal) {
      taskRef.current.openModal(workPackageId);
    }
  }, [taskRef, workPackageId]);

  const renderTask = useCallback(({ item: task, index }) => {
    if (!task) return null;


    return (
      <XStack
        key={task._id || index}
        padding="$2"
        marginVertical="$1"
        backgroundColor={theme ? '#F3F4F6' : '#4B5563'}
        borderRadius="$2"
        alignItems="center"
        justifyContent="space-between"
      >
        <YStack flex={1}>
          <Paragraph
            fontSize={minify ? 12 : 14}
            color={theme ? '#1F2937' : '#F9FAFB'}
            fontWeight="500"
          >
            {task.task_title || 'Untitled Task'}
          </Paragraph>
          {!minify && task.description && (
            <Paragraph
              fontSize={12}
              color={theme ? '#6B7280' : '#9CA3AF'}
              numberOfLines={2}
            >
              {task.description}
            </Paragraph>
          )}
          {!minify && (task.assigned_to || task.due_date) && (
            <XStack gap="$2" marginTop="$1">
              {task.assigned_to && (
                <Paragraph fontSize={10} color={theme ? '#6B7280' : '#9CA3AF'}>
                  👤 {Array.isArray(task.assigned_to) ? task.assigned_to.map((user) => user.name).join(', ') : task.assigned_to.name}
                </Paragraph>
              )}
              {task.due_date && (
                <Paragraph fontSize={10} color={theme ? '#6B7280' : '#9CA3AF'}>
                  📅 {new Date(task.due_date).toLocaleDateString()}
                </Paragraph>
              )}
            </XStack>
          )}
        </YStack>

        {!minify && (
          <Button
            size="$2"
            circular
            backgroundColor="transparent"
            onPress={() => {
              setTaskToDelete(task);
              setDeleteModalVisible(true);
            }}
          >
            <Feather name="trash-2" size={16} color={theme ? '#EF4444' : '#F87171'} />
          </Button>
        )}
      </XStack>
    );
  }, [theme, minify]);

  const tasks = item.task_list || [];

  if (minify) {
    return (
      <YStack gap="$1">
        {tasks.length > 0 ? (
          <>
            {tasks.slice(0, 3).map((task, index) => renderTask({ item: task, index }))}
            {tasks.length > 3 && (
              <Paragraph
                fontSize={10}
                color={theme ? '#6B7280' : '#9CA3AF'}
                textAlign="center"
              >
                +{tasks.length - 3} more tasks
              </Paragraph>
            )}
          </>
        ) : (
          <Paragraph
            fontSize={10}
            color={theme ? '#6B7280' : '#9CA3AF'}
            textAlign="center"
          >
            No tasks
          </Paragraph>
        )}
      </YStack>
    );
  }

  return (
    <>
      <DeleteConfirmModal
        visibleState={deleteModalVisible}
        setState={setDeleteModalVisible}
        title={`"${taskToDelete?.task_title || 'this task'}"`}
        deleteFunction={handleDeleteTask}
      />

      <YStack gap="$2">
        <XStack justifyContent="space-between" alignItems="center">
          <Paragraph
            fontSize={14}
            fontWeight="600"
            color={theme ? '#1F2937' : '#F9FAFB'}
          >
            Tasks ({tasks.length})
          </Paragraph>
          <Button
            size="$3"
            circular
            backgroundColor="$green10"
            onPress={handleAddTask}
          >
            <Feather name="plus" size={16} color="white" />
          </Button>
        </XStack>

        {tasks.length > 0 ? (
          <YStack gap="$1">
            {tasks.map((task, index) => renderTask({ item: task, index }))}
          </YStack>
        ) : (
          <YStack
            padding="$4"
            alignItems="center"
            backgroundColor={theme ? '#F9FAFB' : '#374151'}
            borderRadius="$3"
            borderWidth={1}
            borderColor={theme ? '#E5E7EB' : '#4B5563'}
            borderStyle="dashed"
          >
            <MaterialIcons
              name="assignment"
              size={24}
              color={theme ? '#9CA3AF' : '#6B7280'}
            />
              <Paragraph
                fontSize={12}
                color={theme ? '#6B7280' : '#9CA3AF'}
                textAlign="center"
                marginTop="$1"
              >
                No tasks in this work package
              </Paragraph>
              <Button
                size="$2"
                marginTop="$2"
                backgroundColor="rgb(90,192,234)"
                onPress={handleAddTask}
              >
                <Feather name="plus" size={14} color="white" />
                <Paragraph color="white" fontSize={12} marginLeft="$1">
                  Add Task
                </Paragraph>
              </Button>
            </YStack>
        )}
      </YStack>
    </>
  );
};

export default TaskGroup;
